import { getApiClient, type RequestOpts } from './api-client';
import { QUALITY_METRICS_SERVICE_URL as servicePath } from '../constants/APIconstants';
import { QUALITY_METRIC_ERROR_CODES } from '../constants/quality-metric.constants';

export type ResourceType =
  'knowledge-item' | 'knowledge-category' | 'lab' | 'checklist' | 'external-url' | 'file';

export interface Resource {
  /** ObjectId of the linked entity; absent for `external-url` / `file`. */
  id?: string;
  type: ResourceType;
  title: string;
  /** Required for `external-url` / `file`. */
  url?: string;
  /** The server does not fill this — the client sets it to the acting user. */
  createdBy: string;
  createdAt?: string;
}

/**
 * One entry per calendar month from the metric's `startDate` to the current
 * month. `hasData: false` means the month was interpolated or empty.
 */
export interface TrendPoint {
  year: number;
  month: number;
  total: number;
  totalSuccess: number;
  rate: number | null;
  hasData: boolean;
}

/** Alias kept for the clinician detail read, which named this `TrendBucket`. */
export type TrendBucket = TrendPoint;

export interface QualityMetricResource {
  id: string;
  type: string;
  [key: string]: unknown;
}

export interface QualityMetricDetail {
  id: string;
  title: string;
  description?: string;
  compliance?: string;
  numerator?: string;
  denominator?: string;
  groupGoal: number;
  invert?: boolean;
  minimumRecords?: number;
  startDate?: string;
  totalGroupRecords: number;
  totalGroupSuccess: number;
  groupTrends?: TrendBucket[];
  resources?: QualityMetricResource[];
}

export interface UserMetric {
  userId: string;
  metricId: string;
  groupGoal: number;
  invert?: boolean;
  minimumRecords?: number;
  totalRecords: number;
  totalSuccess: number;
  trends?: TrendBucket[];
  updatedAt?: string;
}

export interface QualityMetricWithUser {
  qualityMetric: QualityMetricDetail | null;
  userMetric: UserMetric | null;
}

export interface GetQualityMetricParams extends RequestOpts {
  id: string;
}

/** The parser the sync service runs on an export. */
export type PullMapper = 'mpog' | 'utmb' | 'keck' | 'gwu' | 'unc';

/**
 * Which external measure feeds a metric, and whether the sync service pulls it.
 * `key` is how the source names the measure — for MPOG, the measure code such
 * as `PONV05`. `mapper` overrides the organization's `mapper.default` (see
 * {@link QualityMetricOrganizationConfiguration}) for this one metric; `null`
 * means use that default, and a pull-enabled metric with neither is skipped.
 */
export interface PullConfiguration {
  key: string;
  enabled: boolean;
  mapper: PullMapper | null;
}

export interface QualityMetricInternalConfigurations {
  pull?: PullConfiguration | null;
}

/**
 * A scheduled pull: when the sync service is asked to fetch a source, and over
 * what window. Stored on the organization's configuration under `<source>.runs`
 * and mirrored by the API as one Cloud Scheduler job per run.
 *
 * `schedule` is a five-field unix cron evaluated in the API's own time zone,
 * and `enabled: false` pauses the job without discarding it. Exactly one window
 * field is set: `lookback` (`"Nmo"`, N from 1 to 24, the last N months on every
 * tick) or `startDate` (`"MM/DD/YYYY"`, from that date until the tick).
 */
export interface QualityMetricRun {
  _id: string;
  enabled: boolean;
  schedule: string;
  lookback?: string;
  startDate?: string;
}

/**
 * How an organization's quality-metric ingestion is set up. Its own document,
 * not a block on the organization, so it is read on its own rather than coming
 * along with the organization itself — always the session organization's.
 *
 * Root only, and the credential value is never returned — `mpog.secretName`
 * only says that credentials exist.
 *
 * `mapper.default` is the parser metrics without one of their own are read
 * with. `mpog.runs` is read-only here: the configuration `PUT` ignores it, and
 * runs are written through the runs endpoints instead.
 */
export interface QualityMetricOrganizationConfiguration {
  id: string;
  organizationId: string;
  mapper?: {
    default?: PullMapper | null;
  };
  mpog?: {
    configurationName?: string | null;
    secretName?: string | null;
    runs?: QualityMetricRun[];
  };
  createdAt: string;
  updatedAt: string;
}

export interface QualityMetric {
  id: string;
  organizationId: string;
  title: string;
  description?: string;
  numerator?: string;
  denominator?: string;
  compliance?: string;
  /** 0–1 target success rate. */
  groupGoal: number;
  totalGroupSuccess: number;
  totalGroupRecords: number;
  /** Records a user needs before their score counts. */
  minimumRecords: number;
  startDate: string;
  resources: Resource[];
  /** `true` = lower is better. */
  invert: boolean;
  /** 0–1000 sort order. */
  priority: number;
  isHidden: boolean;
  internalConfigurations?: QualityMetricInternalConfigurations;
  /**
   * When this metric's *data* last changed — it moves whenever a batch of
   * records lands, from the automated pull or from `POST /:id/records`, and is
   * `null` for a metric that never received any. Edits to the metric's
   * definition do not touch it; that is `updatedAt`.
   */
  lastIngestionAt: string | null;
  createdBy: string;
  updatedBy?: string;
  deletedAt: string | null;
  deletedBy: string | null;
  groupTrends: TrendPoint[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateQualityMetricPayload {
  title: string;
  description?: string;
  numerator?: string;
  denominator?: string;
  compliance?: string;
  groupGoal: number;
  minimumRecords: number;
  startDate: string;
  resources?: Resource[];
  invert: boolean;
  priority?: number;
  isHidden?: boolean;
  /**
   * `PUT /:id` **merges** this object key by key: omit it to keep what is
   * stored, and send `{ pull: null }` to clear the pull configuration.
   *
   * `pull` itself is **replaced whole**, not merged — an omitted `enabled`
   * becomes `false` and an omitted `mapper` becomes `null`. Always send the
   * full `{ key, enabled, mapper }`, even to change one field.
   */
  internalConfigurations?: QualityMetricInternalConfigurations;
}

export type UpdateQualityMetricPayload = Partial<CreateQualityMetricPayload>;

export interface UpdateQualityMetricArgs {
  id: string;
  payload: UpdateQualityMetricPayload;
}

/** A user tracked by a metric, as returned by `GET /:id/users`. */
export interface MetricUser {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  totalRecords: number;
  totalSuccess: number;
  minimumRecords: number;
  /** Success percent with one decimal; `null` when the user has no records. */
  rate: number | null;
  meetsMinimumRecords: boolean;
  trends: TrendPoint[];
}

export interface MetricUsersPage {
  users: MetricUser[];
  total: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

export interface GetOrganizationMetricsParams extends RequestOpts {
  /** Also return hidden metrics. Root only — a non-Root caller gets `403`. */
  includeHidden?: boolean;
}

export interface GetMetricUsersParams extends RequestOpts {
  page?: number;
  pageSize?: number;
}

/** One user's series from `GET /:id/users/trends`. */
export interface MetricUserTrends {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  totalRecords: number;
  totalSuccess: number;
  rate: number | null;
  trends: TrendPoint[];
}

/** One case row, as returned by the two cases-list endpoints. */
export interface MetricCase {
  /** The record's `externalId`, not an ObjectId. */
  id: string;
  /** From `metadata.procedure`; empty string when absent. */
  title: string;
  date: string;
  compliant: boolean;
  /** From `metadata.resultReason`. */
  reason: string | null;
}

export interface MetricCasesPage {
  cases: MetricCase[];
  total: number;
  totalCompliant: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

export interface GetMetricUserCasesParams extends RequestOpts {
  page?: number;
  pageSize?: number;
  /** Omit for all, `true` for compliant only, `false` for non-compliant only. */
  compliant?: boolean;
}

/** A participant is either a matched platform user or an unmatched name. */
export type CaseParticipant =
  { id: string; firstName: string; lastName: string; email: string } | { externalUserName: string };

export interface CaseMetricResult {
  metricId: string;
  title: string;
  meetsGroupGoal: boolean;
  reason: string | null;
  metadata?: Record<string, unknown>;
}

export interface QualityMetricCase {
  externalId: string;
  date: string;
  participants: CaseParticipant[];
  metrics: CaseMetricResult[];
  metadata?: Record<string, unknown>;
}

/**
 * A provider name on the organization's orphan records — records no user is
 * identified with yet. `key` is the normalized name (trimmed, inner whitespace
 * collapsed, upper-cased) that identifications are matched on.
 */
export interface UnmatchedProvider {
  /** The provider that reports the name (`mpog`, `keck`, `gwu`); a name can be listed under two. */
  provider: string;
  externalUserName: string;
  key: string;
  recordsCount: number;
  metricIds: string[];
  lastDate: string;
}

// The endpoints wrap results in `{ qualityMetrics }` / `{ case }`; unwrap here so hooks and consumers get clean records.
interface OrganizationMetricsResponse {
  qualityMetrics: QualityMetric[];
}
interface CaseResponse {
  case: QualityMetricCase;
}

/**
 * Quality-metric API for React Query. Methods throw on failure (axios default),
 * return data directly, and accept `{ signal }` for cancellation.
 *
 * The write endpoints (`createQualityMetric`, `updateQualityMetric`) are
 * Root-only — the backend returns 403 for anyone else. Reads are Viewer-level,
 * and every endpoint 403s unless the organization has the `QUALITY_METRICS`
 * feature enabled.
 *
 * This sits alongside the legacy `quality-metric.service.api.js`, which the
 * clinician-facing page still uses. Consumers should reach this through the
 * hooks in `shared/hooks/quality-metric.hooks`; direct use is only appropriate
 * inside `queryFn` / `mutationFn` bodies.
 */
class QualityMetricRqService {
  /**
   * Metrics in the caller's organization, priority-ordered. Hidden ones are
   * left out unless `includeHidden` is set, which is Root only.
   */
  async getOrganizationMetrics({
    includeHidden = false,
    ...opts
  }: GetOrganizationMetricsParams = {}): Promise<QualityMetric[]> {
    const search = new URLSearchParams({ includeHidden: String(includeHidden) });
    const res = await getApiClient().get<OrganizationMetricsResponse>(
      `${servicePath}list?${search}`,
      opts,
    );
    return res.qualityMetrics ?? [];
  }

  /**
   * One metric plus the caller's own user metric — the clinician detail read.
   * The management screen does not use it: `GET /list` already returns the
   * full metric, `groupTrends` included.
   */
  async getQualityMetric({ id, signal }: GetQualityMetricParams): Promise<QualityMetricWithUser> {
    const { qualityMetric, userMetric } = await getApiClient().get<QualityMetricWithUser>(
      `${servicePath}${id}`,
      { params: { withUserMetric: true }, signal },
    );
    return { qualityMetric: qualityMetric ?? null, userMetric: userMetric ?? null };
  }

  createQualityMetric(payload: CreateQualityMetricPayload): Promise<QualityMetric> {
    return getApiClient().post<QualityMetric>(servicePath, payload);
  }

  updateQualityMetric({ id, payload }: UpdateQualityMetricArgs): Promise<QualityMetric> {
    return getApiClient().put<QualityMetric>(`${servicePath}${id}`, payload);
  }

  /**
   * Users tracked by a metric, most records first. Root-only; the response is
   * paginated, so the caller owns the page state.
   */
  getMetricUsers(
    id: string,
    { page = 1, pageSize = 50, ...opts }: GetMetricUsersParams = {},
  ): Promise<MetricUsersPage> {
    const search = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    return getApiClient().get<MetricUsersPage>(`${servicePath}${id}/users?${search}`, opts);
  }

  /**
   * Monthly trends for several users on one metric. Root-only. The caller is
   * capped at `MAX_COMPARED_USERS` (see `quality-metric.constants`) — more than
   * that is a `400`. Ids the metric does not track are silently omitted, so the
   * response can be shorter than the request.
   */
  async getMetricUsersTrends(
    id: string,
    userIds: string[],
    opts?: RequestOpts,
  ): Promise<MetricUserTrends[]> {
    const search = new URLSearchParams();
    for (const userId of userIds) search.append('userIds', userId);
    const res = await getApiClient().get<{ users: MetricUserTrends[] }>(
      `${servicePath}${id}/users/trends?${search}`,
      opts,
    );
    return res.users ?? [];
  }

  /**
   * One user's cases on a metric, newest first, one entry per `externalId`.
   * Root-only — the Viewer-level `GET /:id/cases` is scoped to the caller.
   */
  getMetricUserCases(
    id: string,
    userId: string,
    { page = 1, pageSize = 200, compliant, ...opts }: GetMetricUserCasesParams = {},
  ): Promise<MetricCasesPage> {
    const search = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (compliant !== undefined) search.append('compliant', String(compliant));
    return getApiClient().get<MetricCasesPage>(
      `${servicePath}${id}/users/${userId}/cases?${search}`,
      opts,
    );
  }

  /**
   * The session organization's ingestion configuration, or `null` when it has none yet
   * — a `404` here is the normal starting state, not a failure, so it is
   * folded into the success arm rather than thrown at the caller.
   *
   * Root only. The api-client rethrows the raw axios error and unwraps the
   * envelope only on success, so the server `code` is read off `response.data`.
   */
  async getOrganizationConfiguration(
    opts?: RequestOpts,
  ): Promise<QualityMetricOrganizationConfiguration | null> {
    try {
      return await getApiClient().get<QualityMetricOrganizationConfiguration>(
        `${servicePath}configuration`,
        opts,
      );
    } catch (error) {
      const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (code === QUALITY_METRIC_ERROR_CODES.CONFIGURATION_NOT_FOUND) return null;
      throw error;
    }
  }

  /**
   * Provider names of the session organization's orphan records, most records
   * first. Names a user already holds are left out. Account admin and Root.
   */
  async getUnmatchedProviders(opts?: RequestOpts): Promise<UnmatchedProvider[]> {
    const res = await getApiClient().get<UnmatchedProvider[]>(
      `${servicePath}unmatched-providers`,
      opts,
    );
    return res ?? [];
  }

  /** One case by its `externalId`, across every metric in the organization. */
  async getCaseById(caseId: string, opts?: RequestOpts): Promise<QualityMetricCase> {
    const res = await getApiClient().get<CaseResponse>(
      `${servicePath}cases/${encodeURIComponent(caseId)}`,
      opts,
    );
    return res.case;
  }
}

export default new QualityMetricRqService();
